// Components
import {
  AvatarWithAddPhoto,
  InstrumentTrays,
  Notes,
  PositionSelector,
} from './CardViewComponents'
import { DataTable } from 'components/molecules/DataTable/DataTable'
import AutocompleteInput from 'components/organisms/AutocompleteInput/AutocompleteInput'
import Header from 'components/molecules/Header/Header'
import CardItem from '../CardItem/CardItem'
import Select from 'components/molecules/Select/Select'
import Button from 'components/molecules/Button/Button'
import QuantityModal from './CardViewComponents/QuantityModal'
import TagFilter from './CardViewComponents/TagFilter'
import AddSalesRep from './AddSalesRep/AddSalesRep'
import { RepItem } from '../RepItem/RepItem'
import Warning from 'components/molecules/Warning/Warning'
import { CompanySearchBar } from 'components/CompanySearchBar'

// MUI
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Typography from '@mui/material/Typography'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import {
  Checkbox,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  IconButton,
  RadioGroup,
  Radio,
  MenuItem,
  Select as MuiSelect,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'

// Logic
import useCardViewLogic from './CardView.logic'

// Types
import { Option } from 'common/types'
import { MedicationTiming } from './CardView.types'

// Style
import './CardView.scss'

// Other
import classNames from 'classnames'
import { PositioningDevices, ProductItem, SkinPreps } from './CardView.types'
import {
  consumablesTableHeaders,
  drillOptions,
  positioningDevices,
  skinPreps,
} from './constants'

const CardView = ({ mode }: { mode: 'create' | 'view' | 'clone' }) => {
  const {
    nMRep,
    canSave,
    isMobile,
    isLoading,
    repsOptions,
    medications,
    availableTags,
    isLoadingCard,
    salesRepModal,
    isNMRepEnabled,
    preferenceCard,
    equipmentItems,
    isCreatingCard,
    selectedProduct,
    selectedVendor,
    isUpdatingCard,
    surgeonsOptions,
    perfusionistRep,
    instrumentTrays,
    selectedProducts,
    procedureDetails,
    selectedCompanies,
    quantityModalOpen,
    medicationOptions,
    isLoadingSurgeons,
    implantDescription,
    consumablesOptions,
    biologicDescription,
    isLoadingAddNewRep,
    surgeonInformation,
    selectedFilterTags,
    isLoadingConsumables,
    consumablesTableData,
    groupedRepsByCompany,
    medicationSearchValue,
    procedureTypesOptions,
    instrumentTrayOptions,
    consumablesSearchValue,
    debouncedSetSearchValue,
    isLoadingProcedureTypes,
    isPerfusionistRepEnabled,
    showPreferenceCardNameError,
    showPascPreferenceCardFeatures,
    isLoadingConsumablesProductsData,
    navigate,
    handleClickBack,
    resetMedication,
    setSalesRepModal,
    handleInputChange,
    handleAddProducts,
    setEquipmentItems,
    handleAddCompanies,
    setSelectedVendor,
    handleAddSalesRep,
    setInstrumentTrays,
    handleProductSelect,
    handleDeleteProduct,
    handleDeleteCompany,
    handlePositionChange,
    handleNotifySalesRep,
    handleSkinPrepChange,
    handleAddNewSalesRep,
    setQuantityModalOpen,
    createPreferenceCard,
    handleDeleteSalesRep,
    handleInputNameChange,
    handleTagFilterSelect,
    handleMedicationSelect,
    setImplantDescription,
    setBiologicDescription,
    handleRemoveMedication,
    handleMedicationsChange,
    handleMeasurementConfirm,
    handleEquipmentItemChange,
    handleChangeMedicationTiming,
    handlePositioningDeviceChange,
    handleSurgeonInformationChange,
    handlePositioningDeviceOtherText,
    debouncedSetMedicationSearchValue,
  } = useCardViewLogic({ mode })

  const isCreateMode = mode === 'create'
  const isViewMode = mode === 'view'
  const isCloneMode = mode === 'clone'

  return (
    <div
      className={classNames('preference-card-view', {
        'is-mobile': isMobile,
      })}
    >
      <Header
        parent="Reports"
        title="Preference card"
        onBackClick={handleClickBack}
        hasBack
        isMobile={isMobile}
        actions={
          isViewMode
            ? [
                {
                  title: 'Clone',
                  onClick: () =>
                    navigate(`/preference-card/${preferenceCard.id}/clone`),
                  isLoading: false,
                },
              ]
            : isCreateMode || isCloneMode
            ? [
                {
                  title: 'Save',
                  onClick: () => createPreferenceCard(),
                  isLoading: isCreatingCard || isUpdatingCard,
                  disabled: !canSave,
                },
              ]
            : []
        }
      />

      <div className="preference-card-header">
        {isViewMode &&
          (surgeonInformation?.physicianName?.name ||
            surgeonInformation?.procedureName) && (
            <CardItem
              isHeader
              surgeon={surgeonInformation?.physicianName?.name ?? ''}
              procedure={surgeonInformation?.procedureName ?? ''}
              isSaving={isCreatingCard || isUpdatingCard}
              isLoading={isLoadingCard}
            />
          )}
      </div>

      <div className="body">
        <div className="preference-card-name">
          <TextField
            label="Preference Card Name"
            required
            value={preferenceCard.name || ''}
            onChange={(e) => handleInputNameChange(e.target.value)}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
            error={showPreferenceCardNameError}
            helperText={
              showPreferenceCardNameError
                ? 'Please enter a name for the preference card to save.'
                : ''
            }
            disabled={isLoadingConsumables || isLoadingCard}
          />
        </div>
        <div>
          {/* Surgeon information */}
          <Accordion className="accordion">
            <AccordionSummary
              expandIcon={<ArrowDownwardIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography>Surgeon information</Typography>
            </AccordionSummary>
            <AccordionDetails className="accordion-details">
              <div className="details-container">
                <div className="section">
                  <Select
                    isLoading={isLoadingSurgeons}
                    label="Physician Name"
                    options={surgeonsOptions || []}
                    onSelect={(value) => {
                      handleSurgeonInformationChange('physicianName', value)
                    }}
                    selectedValue={
                      (surgeonInformation?.physicianName?.name as string) ?? ''
                    }
                  />
                  <AutocompleteInput
                    variant="outlined"
                    isLoading={isLoadingProcedureTypes}
                    label="Procedure Name"
                    options={procedureTypesOptions || []}
                    value={surgeonInformation?.procedureName ?? ''}
                    handleChangeFormData={(e) => {
                      const value = e.target.value as Option
                      handleSurgeonInformationChange(
                        'procedureName',
                        value.name
                      )
                    }}
                    onTextInputChange={(e) => {
                      const value = e.target.value as string
                      handleSurgeonInformationChange('procedureName', value)
                    }}
                    sx={{ my: 0 }}
                  />

                  <Select
                    label="Dominant Hand"
                    options={[
                      { id: 1, name: 'Left' },
                      { id: 2, name: 'Right' },
                    ]}
                    onSelect={(value) => {
                      handleSurgeonInformationChange('dominantHand', value)
                    }}
                    selectedValue={surgeonInformation?.dominantHand?.name ?? ''}
                  />

                  <TextField
                    label="Glove Size and Type"
                    placeholder="Glove Size and Type"
                    value={surgeonInformation?.gloveSize}
                    onChange={(e) => {
                      handleSurgeonInformationChange(
                        'gloveSize',
                        e.target.value
                      )
                    }}
                    InputLabelProps={{
                      shrink: surgeonInformation?.gloveSize ? true : false,
                    }}
                  />
                  <div className="checkbox-group">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={surgeonInformation?.gloveType === 'Single'}
                          onChange={(e) => {
                            handleSurgeonInformationChange(
                              'gloveType',
                              'Single'
                            )
                          }}
                        />
                      }
                      label="Single"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={surgeonInformation?.gloveType === 'Double'}
                          onChange={(e) => {
                            handleSurgeonInformationChange(
                              'gloveType',
                              'Double'
                            )
                          }}
                        />
                      }
                      label="Double"
                    />
                  </div>
                </div>
                <div className="section">
                  <AvatarWithAddPhoto
                    disabled={isLoading}
                    defaultImage={surgeonInformation?.picture}
                    onImageChange={(image) => {
                      if (image)
                        handleSurgeonInformationChange('picture', image)
                    }}
                  />
                </div>
              </div>
              <Divider />
              <Notes
                title="Other Notes"
                initialNotes={surgeonInformation?.notes}
                onNotesChange={(notes) =>
                  handleSurgeonInformationChange('notes', notes)
                }
              />
            </AccordionDetails>
          </Accordion>

          {/* Procedure information */}
          <Accordion className="accordion">
            <AccordionSummary
              expandIcon={<ArrowDownwardIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography>Procedure information</Typography>
            </AccordionSummary>
            <AccordionDetails className="accordion-details">
              <div className="details-container">
                <div className="section">
                  <PositionSelector
                    onChange={handlePositionChange}
                    selected={preferenceCard.procedureDetails?.selectedPosition}
                  />

                  <div>
                    <div className="item">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              preferenceCard.procedureDetails?.tableRotation ===
                              true
                            }
                            onChange={(e) => {
                              handleInputChange(
                                'tableRotation',
                                e.target.checked
                              )
                            }}
                          />
                        }
                        label="Table Rotation"
                      />
                    </div>
                    {preferenceCard.procedureDetails?.tableRotation && (
                      <div className="table-rotation-options">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Typography>Left</Typography>
                          <Switch
                            onChange={(e) => {
                              handleInputChange(
                                'tableRotationSide',
                                e.target.checked ? 'Right' : 'Left'
                              )
                            }}
                            checked={
                              preferenceCard.procedureDetails
                                .tableRotationSide === 'Right'
                            }
                          />
                          <Typography>Right</Typography>
                        </div>
                        <div>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={
                                  preferenceCard.procedureDetails
                                    .tableRotationDegree === '45'
                                }
                                onChange={(e) => {
                                  handleInputChange(
                                    'tableRotationDegree',
                                    e.target.checked ? '45' : ''
                                  )
                                }}
                              />
                            }
                            label="45°"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={
                                  preferenceCard.procedureDetails
                                    .tableRotationDegree === '90'
                                }
                                onChange={(e) => {
                                  handleInputChange(
                                    'tableRotationDegree',
                                    e.target.checked ? '90' : ''
                                  )
                                }}
                              />
                            }
                            label="90°"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={
                                  preferenceCard.procedureDetails
                                    .tableRotationDegree === '180'
                                }
                                onChange={(e) => {
                                  handleInputChange(
                                    'tableRotationDegree',
                                    e.target.checked ? '180' : ''
                                  )
                                }}
                              />
                            }
                            label="180°"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="section">
                  <Typography variant="h3">Skin Prep</Typography>

                  <div className="checkbox-group">
                    {skinPreps.map((item) => (
                      <FormControlLabel
                        key={item.id}
                        control={
                          <Checkbox
                            checked={
                              procedureDetails.skinPrep?.[
                                item.id as keyof SkinPreps
                              ] ?? false
                            }
                            onChange={(e) => {
                              handleSkinPrepChange(
                                item.id as keyof SkinPreps,
                                e.target.checked
                              )
                            }}
                          />
                        }
                        label={item.name}
                      />
                    ))}
                  </div>
                  <Divider />

                  <Typography variant="h3">Medication</Typography>
                  {!showPascPreferenceCardFeatures && (
                    <>
                      {/* Lidocaine */}
                      <Accordion className="accordion">
                        <AccordionSummary expandIcon={<ArrowDownwardIcon />}>
                          <div className="row">
                            <Typography>Lidocaine</Typography>
                            {medications?.lidocaine?.concentration && (
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  resetMedication('lidocaine')
                                }}
                                size="small"
                                variant="outlined"
                              >
                                <Typography variant="caption">Reset</Typography>
                              </Button>
                            )}
                          </div>
                        </AccordionSummary>
                        <AccordionDetails className="accordion-details">
                          <Typography>Concentration:</Typography>
                          <RadioGroup
                            row
                            value={medications?.lidocaine?.concentration || ''}
                            onChange={(e) =>
                              handleMedicationsChange(
                                'lidocaine',
                                'concentration',
                                e.target.value
                              )
                            }
                          >
                            <FormControlLabel
                              value="0.5%"
                              control={<Radio />}
                              label="0.5%"
                            />
                            <FormControlLabel
                              value="1%"
                              control={<Radio />}
                              label="1%"
                            />
                            <FormControlLabel
                              value="2%"
                              control={<Radio />}
                              label="2%"
                            />
                          </RadioGroup>
                          <TextField
                            label="Volume (cc)"
                            value={medications?.lidocaine?.volume}
                            onChange={(e) =>
                              handleMedicationsChange(
                                'lidocaine',
                                'volume',
                                e.target.value
                              )
                            }
                            fullWidth
                            margin="normal"
                          />
                        </AccordionDetails>
                      </Accordion>

                      {/* Lidocaine with Epinephrine */}
                      <Accordion className="accordion">
                        <AccordionSummary expandIcon={<ArrowDownwardIcon />}>
                          <div className="row">
                            <Typography>Lidocaine with Epinephrine</Typography>
                            {medications?.lidocaineWithEpi?.concentration && (
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  resetMedication('lidocaineWithEpi')
                                }}
                                size="small"
                                variant="outlined"
                              >
                                <Typography variant="caption">Reset</Typography>
                              </Button>
                            )}
                          </div>
                        </AccordionSummary>
                        <AccordionDetails className="accordion-details">
                          <Typography>Concentration:</Typography>
                          <RadioGroup
                            row
                            value={
                              medications?.lidocaineWithEpi?.concentration || ''
                            }
                            onChange={(e) =>
                              handleMedicationsChange(
                                'lidocaineWithEpi',
                                'concentration',
                                e.target.value
                              )
                            }
                          >
                            <FormControlLabel
                              value="0.5%"
                              control={<Radio />}
                              label="0.5%"
                            />
                            <FormControlLabel
                              value="1%"
                              control={<Radio />}
                              label="1%"
                            />
                            <FormControlLabel
                              value="2%"
                              control={<Radio />}
                              label="2%"
                            />
                          </RadioGroup>
                          <Typography>Epinephrine Concentration:</Typography>
                          <RadioGroup
                            row
                            value={
                              medications?.lidocaineWithEpi?.epiConcentration ||
                              ''
                            }
                            onChange={(e) =>
                              handleMedicationsChange(
                                'lidocaineWithEpi',
                                'epiConcentration',
                                e.target.value
                              )
                            }
                          >
                            <FormControlLabel
                              value="1:100,000"
                              control={<Radio />}
                              label="1:100,000"
                            />
                            <FormControlLabel
                              value="1:200,000"
                              control={<Radio />}
                              label="1:200,000"
                            />
                          </RadioGroup>
                          <TextField
                            label="Volume (cc)"
                            value={medications?.lidocaineWithEpi?.volume}
                            onChange={(e) =>
                              handleMedicationsChange(
                                'lidocaineWithEpi',
                                'volume',
                                e.target.value
                              )
                            }
                            fullWidth
                            margin="normal"
                          />
                        </AccordionDetails>
                      </Accordion>

                      {/* Marcaine */}
                      <Accordion className="accordion">
                        <AccordionSummary expandIcon={<ArrowDownwardIcon />}>
                          <div className="row">
                            <Typography>Marcaine</Typography>
                            {medications?.marcaine?.concentration && (
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  resetMedication('marcaine')
                                }}
                                size="small"
                                variant="outlined"
                              >
                                <Typography variant="caption">Reset</Typography>
                              </Button>
                            )}
                          </div>
                        </AccordionSummary>
                        <AccordionDetails className="accordion-details">
                          <Typography>Concentration:</Typography>
                          <RadioGroup
                            row
                            value={medications?.marcaine?.concentration || ''}
                            onChange={(e) =>
                              handleMedicationsChange(
                                'marcaine',
                                'concentration',
                                e.target.value
                              )
                            }
                          >
                            <FormControlLabel
                              value="0.25%"
                              control={<Radio />}
                              label="0.25%"
                            />
                            <FormControlLabel
                              value="0.5%"
                              control={<Radio />}
                              label="0.5%"
                            />
                          </RadioGroup>
                          <TextField
                            label="Volume (cc)"
                            value={medications?.marcaine?.volume}
                            onChange={(e) =>
                              handleMedicationsChange(
                                'marcaine',
                                'volume',
                                e.target.value
                              )
                            }
                            fullWidth
                            margin="normal"
                          />
                        </AccordionDetails>
                      </Accordion>

                      {/* Marcaine with Epinephrine */}
                      <Accordion className="accordion">
                        <AccordionSummary expandIcon={<ArrowDownwardIcon />}>
                          <div className="row">
                            <Typography>Marcaine with Epinephrine</Typography>
                            {medications?.marcaineWithEpi?.concentration && (
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  resetMedication('marcaineWithEpi')
                                }}
                                size="small"
                                variant="outlined"
                              >
                                <Typography variant="caption">Reset</Typography>
                              </Button>
                            )}
                          </div>
                        </AccordionSummary>
                        <AccordionDetails className="accordion-details">
                          <Typography>Concentration:</Typography>
                          <RadioGroup
                            row
                            value={
                              medications?.marcaineWithEpi?.concentration || ''
                            }
                            onChange={(e) =>
                              handleMedicationsChange(
                                'marcaineWithEpi',
                                'concentration',
                                e.target.value
                              )
                            }
                          >
                            <FormControlLabel
                              value="0.25%"
                              control={<Radio />}
                              label="0.25%"
                            />
                            <FormControlLabel
                              value="0.5%"
                              control={<Radio />}
                              label="0.5%"
                            />
                          </RadioGroup>

                          <Typography>Epinephrine Concentration:</Typography>
                          <RadioGroup
                            row
                            value={
                              medications?.marcaineWithEpi?.epiConcentration ||
                              ''
                            }
                            onChange={(e) =>
                              handleMedicationsChange(
                                'marcaineWithEpi',
                                'epiConcentration',
                                e.target.value
                              )
                            }
                          >
                            <FormControlLabel
                              value="1:100,000"
                              control={<Radio />}
                              label="1:100,000"
                            />
                            <FormControlLabel
                              value="1:200,000"
                              control={<Radio />}
                              label="1:200,000"
                            />
                          </RadioGroup>
                          <TextField
                            label="Volume (cc)"
                            value={medications?.marcaineWithEpi?.volume}
                            onChange={(e) =>
                              handleMedicationsChange(
                                'marcaineWithEpi',
                                'volume',
                                e.target.value
                              )
                            }
                            fullWidth
                            margin="normal"
                          />
                        </AccordionDetails>
                      </Accordion>

                      {/* Other */}
                      <Accordion className="accordion">
                        <AccordionSummary expandIcon={<ArrowDownwardIcon />}>
                          <div className="row">
                            <Typography>Other</Typography>
                            {medications?.other && (
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  resetMedication('other')
                                }}
                                size="small"
                                variant="outlined"
                              >
                                <Typography variant="caption">Reset</Typography>
                              </Button>
                            )}
                          </div>
                        </AccordionSummary>
                        <AccordionDetails className="accordion-details">
                          <TextField
                            label="Other"
                            value={medications?.other}
                            onChange={(e) =>
                              handleMedicationsChange(
                                'other',
                                'other',
                                e.target.value
                              )
                            }
                            fullWidth
                            margin="normal"
                          />
                        </AccordionDetails>
                      </Accordion>
                    </>
                  )}

                  {showPascPreferenceCardFeatures && (
                    <Accordion className="accordion">
                      <AccordionSummary expandIcon={<ArrowDownwardIcon />}>
                        <Typography>Medication Search</Typography>
                      </AccordionSummary>
                      <AccordionDetails className="accordion-details">
                        <div className="medication-search-container">
                          <div className="search-box">
                            <AutocompleteInput
                              handleChangeFormData={(e) =>
                                handleMedicationSelect(e.target.value as Option)
                              }
                              onTextInputChange={(e) =>
                                debouncedSetMedicationSearchValue(
                                  e.target.value as string
                                )
                              }
                              value={medicationSearchValue}
                              options={medicationOptions}
                              label="Search medications"
                              variant="outlined"
                              placeholder="Start typing to search..."
                              isLoading={false}
                              clearOnSelect={true}
                            />
                          </div>

                          {/* Selected Medications Table */}
                          {medications?.selectedMedications &&
                            medications.selectedMedications.length > 0 && (
                              <TableContainer
                                component={Paper}
                                style={{ marginTop: 10 }}
                              >
                                <Table>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Medication</TableCell>
                                      <TableCell>When Used</TableCell>
                                      <TableCell align="right">
                                        Actions
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {medications.selectedMedications.map(
                                      (med) => (
                                        <TableRow key={med.id}>
                                          <TableCell>{med.name}</TableCell>
                                          <TableCell>
                                            <FormControl fullWidth>
                                              <MuiSelect
                                                value={med.timing}
                                                size="small"
                                                onChange={(e) =>
                                                  handleChangeMedicationTiming(
                                                    med.id,
                                                    e.target
                                                      .value as MedicationTiming
                                                  )
                                                }
                                              >
                                                <MenuItem value="On the field">
                                                  On the field
                                                </MenuItem>
                                                <MenuItem value="Off the field">
                                                  Off the field
                                                </MenuItem>
                                              </MuiSelect>
                                            </FormControl>
                                          </TableCell>
                                          <TableCell align="right">
                                            <IconButton
                                              onClick={() =>
                                                handleRemoveMedication(med.id)
                                              }
                                              size="small"
                                              color="error"
                                            >
                                              <DeleteIcon />
                                            </IconButton>
                                          </TableCell>
                                        </TableRow>
                                      )
                                    )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )}
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  <Divider />

                  <Typography variant="h3">Positioning Devices</Typography>
                  <div className="item">
                    {positioningDevices.map((device) => (
                      <FormControlLabel
                        key={device.id}
                        control={
                          <Checkbox
                            checked={
                              typeof procedureDetails.positioningDevices?.[
                                device.id as keyof PositioningDevices
                              ] === 'boolean'
                                ? (procedureDetails.positioningDevices?.[
                                    device.id as keyof PositioningDevices
                                  ] as boolean)
                                : false
                            }
                            onChange={(e) =>
                              handlePositioningDeviceChange(
                                device.id as keyof PositioningDevices,
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={device.name}
                      />
                    ))}
                    {procedureDetails.positioningDevices?.other && (
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder="Please specify"
                        value={
                          procedureDetails.positioningDevices?.otherText ?? ''
                        }
                        onChange={(e) =>
                          handlePositioningDeviceOtherText(e.target.value)
                        }
                        sx={{ mt: 1 }}
                      />
                    )}
                  </div>
                  <Divider />

                  <div className="row">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={procedureDetails.DVTProhylaxis === true}
                          onChange={(e) =>
                            handleInputChange('DVTProhylaxis', e.target.checked)
                          }
                        />
                      }
                      label="DVT Prohylaxis"
                    />
                  </div>

                  {procedureDetails.DVTProhylaxis && (
                    <Divider variant="middle" />
                  )}

                  <div className="checkbox-group">
                    {procedureDetails.DVTProhylaxis && (
                      <>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={procedureDetails.dvtType === 'Stockings'}
                              onChange={(e) =>
                                handleInputChange(
                                  'dvtType',
                                  e.target.checked ? 'Stockings' : undefined
                                )
                              }
                            />
                          }
                          label="Stockings"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={procedureDetails.dvtType === 'SCD'}
                              onChange={(e) =>
                                handleInputChange(
                                  'dvtType',
                                  e.target.checked ? 'SCD' : undefined
                                )
                              }
                            />
                          }
                          label="SCD"
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={procedureDetails.dvtType === 'Boots'}
                              onChange={(e) =>
                                handleInputChange(
                                  'dvtType',
                                  e.target.checked ? 'Boots' : undefined
                                )
                              }
                            />
                          }
                          label="Boots"
                        />
                      </>
                    )}
                  </div>

                  {procedureDetails.DVTProhylaxis &&
                    procedureDetails.dvtType && <Divider variant="middle" />}

                  {procedureDetails.DVTProhylaxis &&
                    procedureDetails.dvtType && (
                      <div className="checkbox-group">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={procedureDetails.anticoagSide === 'Left'}
                              onChange={(e) =>
                                handleInputChange(
                                  'anticoagSide',
                                  e.target.checked ? 'Left' : undefined
                                )
                              }
                            />
                          }
                          label="Left"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                procedureDetails.anticoagSide === 'Right'
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  'anticoagSide',
                                  e.target.checked ? 'Right' : undefined
                                )
                              }
                            />
                          }
                          label="Right"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                procedureDetails.anticoagSide === 'Bilateral'
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  'anticoagSide',
                                  e.target.checked ? 'Bilateral' : undefined
                                )
                              }
                            />
                          }
                          label="Bilateral"
                        />
                      </div>
                    )}
                </div>
              </div>
              <Divider />
              <Notes
                title="Other Notes"
                initialNotes={procedureDetails.notes}
                onNotesChange={(notes) => handleInputChange('notes', notes)}
              />
            </AccordionDetails>
          </Accordion>

          {/* Equipment information */}
          <Accordion className="accordion">
            <AccordionSummary
              expandIcon={<ArrowDownwardIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography>Equipment information</Typography>
            </AccordionSummary>
            <AccordionDetails className="accordion-details">
              <div className="details-container col half-width equipments">
                <div className="row">
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]
                              ?.Monopolar as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Monopolar',
                              e.target.checked
                            )
                            if (!e.target.checked) {
                              handleEquipmentItemChange(0, 'Cut(Monopolar)', '')
                              handleEquipmentItemChange(
                                0,
                                'Coag(Monopolar)',
                                ''
                              )
                            }
                          }}
                        />
                      }
                      label="Monopolar"
                    />
                  </div>
                  {(equipmentItems?.options?.[0]?.Monopolar as boolean) && (
                    <>
                      <div className="text-box item">
                        <Typography variant="h3">Cut</Typography>
                        <TextField
                          size="small"
                          label="Settings"
                          placeholder="Settings"
                          value={
                            equipmentItems?.options?.[0]?.['Cut(Monopolar)'] ??
                            ''
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Cut(Monopolar)',
                              e.target.value
                            )
                          }}
                        />
                      </div>
                      <div className="text-box item">
                        <Typography variant="h3">Coag</Typography>
                        <TextField
                          size="small"
                          label="Settings"
                          placeholder="Settings"
                          value={
                            equipmentItems?.options?.[0]?.['Coag(Monopolar)'] ??
                            ''
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Coag(Monopolar)',
                              e.target.value
                            )
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
                <Divider />
                <div className="row">
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]
                              ?.Bipolar as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Bipolar',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="Bipolar"
                    />
                  </div>
                  {(equipmentItems?.options?.[0]?.Bipolar as boolean) && (
                    <div className="text-box item">
                      <Typography variant="h3">Coag</Typography>
                      <TextField
                        size="small"
                        label="Settings"
                        placeholder="Settings"
                        value={
                          equipmentItems?.options?.[0]?.['Coag(Bipolar)'] ?? ''
                        }
                        onChange={(e) => {
                          handleEquipmentItemChange(
                            0,
                            'Coag(Bipolar)',
                            e.target.value
                          )
                        }}
                      />
                    </div>
                  )}
                </div>
                <Divider />
                <div className="row">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          (equipmentItems?.options?.[0]?.[
                            'Suction Bovie'
                          ] as boolean) ?? false
                        }
                        onChange={(e) => {
                          handleEquipmentItemChange(
                            0,
                            'Suction Bovie',
                            e.target.checked
                          )
                        }}
                      />
                    }
                    label="Suction Bovie"
                  />
                </div>
                <Divider />
                <div className="row">
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.[
                              'Spine table'
                            ] as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Spine table',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="Spine table"
                    />
                  </div>
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.[
                              'Hand table'
                            ] as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Hand table',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="Hand table"
                    />
                  </div>
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]
                              ?.Mayfield as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Mayfield',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="Mayfield"
                    />
                  </div>
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]
                              ?.Horseshoe as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Horseshoe',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="Horseshoe"
                    />
                  </div>
                </div>
                <Divider />
                <div className="row">
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.[
                              'GI Cart'
                            ] as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'GI Cart',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="GI Cart"
                    />
                  </div>
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.[
                              'Arthroscopy Tower'
                            ] as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Arthroscopy Tower',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="Arthroscopy Tower"
                    />
                  </div>
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.[
                              'Laparoscopy Tower'
                            ] as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Laparoscopy Tower',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="Laparoscopy Tower"
                    />
                  </div>
                </div>
                <Divider />
                <div className="row">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          (equipmentItems?.options?.[0]?.Robot as boolean) ??
                          false
                        }
                        onChange={(e) => {
                          handleEquipmentItemChange(
                            0,
                            'Robot',
                            e.target.checked
                          )
                        }}
                      />
                    }
                    label="Robot"
                  />
                  {(equipmentItems?.options?.[0]?.Robot as boolean) && (
                    <TextField
                      size="small"
                      label="Settings"
                      placeholder="Settings"
                      value={
                        equipmentItems?.options?.[0]?.['Robot Settings'] ?? ''
                      }
                      onChange={(e) => {
                        handleEquipmentItemChange(
                          0,
                          'Robot Settings',
                          e.target.value
                        )
                      }}
                      InputLabelProps={
                        equipmentItems?.options?.[0]?.['Robot Settings']
                          ? { shrink: true }
                          : { shrink: false }
                      }
                    />
                  )}
                </div>
                <Divider />
                <div className="row">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          (equipmentItems?.options?.[0]?.Drill as boolean) ??
                          false
                        }
                        onChange={(e) => {
                          handleEquipmentItemChange(
                            0,
                            'Drill',
                            e.target.checked
                          )
                        }}
                      />
                    }
                    label="Drill"
                  />
                  <div className="width-1">
                    {(equipmentItems?.options?.[0]?.Drill as boolean) && (
                      <Select
                        selectedValue={
                          (equipmentItems?.options?.[0]?.[
                            'Drill Type'
                          ] as string) ?? ''
                        }
                        placeholder="Drill type"
                        options={drillOptions}
                        label="Drill type"
                        onSelect={(option) =>
                          handleEquipmentItemChange(
                            0,
                            'Drill Type',
                            option.name
                          )
                        }
                        size="small"
                      />
                    )}
                  </div>
                </div>
                <Divider />
                <div className="row">
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]
                              ?.Sonopet as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Sonopet',
                              e.target.checked
                            )
                            if (!e.target.checked) {
                              handleEquipmentItemChange(0, 'Power(Sonopet)', '')
                              handleEquipmentItemChange(
                                0,
                                'Suction(Sonopet)',
                                ''
                              )
                              handleEquipmentItemChange(
                                0,
                                'Irrigation(Sonopet)',
                                ''
                              )
                            }
                          }}
                        />
                      }
                      label="Sonopet"
                    />
                  </div>
                  {(equipmentItems?.options?.[0]?.Sonopet as boolean) && (
                    <>
                      <div className="text-box item">
                        <TextField
                          size="small"
                          label="Power"
                          placeholder="Power"
                          value={
                            equipmentItems?.options?.[0]?.['Power(Sonopet)'] ??
                            ''
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Power(Sonopet)',
                              e.target.value
                            )
                          }}
                        />
                      </div>
                      <div className="text-box item">
                        <TextField
                          size="small"
                          label="Suction"
                          placeholder="Suction"
                          value={
                            equipmentItems?.options?.[0]?.[
                              'Suction(Sonopet)'
                            ] ?? ''
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Suction(Sonopet)',
                              e.target.value
                            )
                          }}
                        />
                      </div>
                      <div className="text-box item">
                        <TextField
                          size="small"
                          label="Irrigation"
                          placeholder="Irrigation"
                          value={
                            equipmentItems?.options?.[0]?.[
                              'Irrigation(Sonopet)'
                            ] ?? ''
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Irrigation(Sonopet)',
                              e.target.value
                            )
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
                <Divider />
                <div className="row">
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.CUSA as boolean) ??
                            false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'CUSA',
                              e.target.checked
                            )
                            if (!e.target.checked) {
                              handleEquipmentItemChange(0, 'Power(CUSA)', '')
                              handleEquipmentItemChange(0, 'Suction(CUSA)', '')
                              handleEquipmentItemChange(
                                0,
                                'Irrigation(CUSA)',
                                ''
                              )
                            }
                          }}
                        />
                      }
                      label="CUSA"
                    />
                  </div>
                  {(equipmentItems?.options?.[0]?.CUSA as boolean) && (
                    <>
                      <div className="text-box item">
                        {/* <Typography variant="h3">Power</Typography> */}
                        <TextField
                          size="small"
                          label="Power"
                          placeholder="Power"
                          value={
                            equipmentItems?.options?.[0]?.['Power(CUSA)'] ?? ''
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Power(CUSA)',
                              e.target.value
                            )
                          }}
                        />
                      </div>
                      <div className="text-box item">
                        {/* <Typography variant="h3">Suction</Typography> */}
                        <TextField
                          size="small"
                          label="Suction"
                          placeholder="Suction"
                          value={
                            equipmentItems?.options?.[0]?.['Suction(CUSA)'] ??
                            ''
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Suction(CUSA)',
                              e.target.value
                            )
                          }}
                        />
                      </div>
                      <div className="text-box item">
                        {/* <Typography variant="h3">Irrigation</Typography> */}
                        <TextField
                          size="small"
                          label="Irrigation"
                          placeholder="Irrigation"
                          value={
                            equipmentItems?.options?.[0]?.[
                              'Irrigation(CUSA)'
                            ] ?? ''
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Irrigation(CUSA)',
                              e.target.value
                            )
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
                <Divider />
                <div className="row">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          (equipmentItems?.options?.[0]?.Laser as boolean) ??
                          false
                        }
                        onChange={(e) => {
                          handleEquipmentItemChange(
                            0,
                            'Laser',
                            e.target.checked
                          )
                        }}
                      />
                    }
                    label="Laser"
                  />
                  {(equipmentItems?.options?.[0]?.Laser as boolean) && (
                    <TextField
                      size="small"
                      label="Laser notes"
                      placeholder="Laser notes"
                      value={
                        equipmentItems?.options?.[0]?.['Laser notes'] ?? ''
                      }
                      onChange={(e) => {
                        handleEquipmentItemChange(
                          0,
                          'Laser notes',
                          e.target.value
                        )
                      }}
                      InputLabelProps={
                        equipmentItems?.options?.[0]?.['Laser notes']
                          ? { shrink: true }
                          : { shrink: false }
                      }
                    />
                  )}
                </div>
                <Divider />
                <div className="row">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          (equipmentItems?.options?.[0]?.[
                            'Cell Saver'
                          ] as boolean) ?? false
                        }
                        onChange={(e) => {
                          handleEquipmentItemChange(
                            0,
                            'Cell Saver',
                            e.target.checked
                          )
                        }}
                      />
                    }
                    label="Cell Saver"
                  />
                  {(equipmentItems?.options?.[0]?.[
                    'Cell Saver'
                  ] as boolean) && (
                    <TextField
                      size="small"
                      label="Cell Saver notes"
                      placeholder="Cell Saver notes"
                      value={
                        equipmentItems?.options?.[0]?.['Cell Saver notes'] ?? ''
                      }
                      onChange={(e) => {
                        handleEquipmentItemChange(
                          0,
                          'Cell Saver notes',
                          e.target.value
                        )
                      }}
                      InputLabelProps={
                        equipmentItems?.options?.[0]?.['Cell Saver notes']
                          ? { shrink: true }
                          : { shrink: false }
                      }
                    />
                  )}
                </div>
                <Divider />
                <div className="row">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          (equipmentItems?.options?.[0]?.[
                            'C Arm/ Flouro'
                          ] as boolean) ?? false
                        }
                        onChange={(e) => {
                          handleEquipmentItemChange(
                            0,
                            'C Arm/ Flouro',
                            e.target.checked
                          )
                        }}
                      />
                    }
                    label="C Arm/ Flouro"
                  />
                </div>
                <Divider />
                <div className="row">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          (equipmentItems?.options?.[0]
                            ?.Microscope as boolean) ?? false
                        }
                        onChange={(e) => {
                          handleEquipmentItemChange(
                            0,
                            'Microscope',
                            e.target.checked
                          )
                        }}
                      />
                    }
                    label="Microscope"
                  />
                </div>
                <Divider />
                <div className="row">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          (equipmentItems?.options?.[0]
                            ?.Neuromonitoring as boolean) ?? false
                        }
                        onChange={(e) => {
                          handleEquipmentItemChange(
                            0,
                            'Neuromonitoring',
                            e.target.checked
                          )
                        }}
                      />
                    }
                    label="Neuromonitoring"
                  />
                </div>
                <Divider />
                <div className="row">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          (equipmentItems?.options?.[0]?.Stealth as boolean) ??
                          false
                        }
                        onChange={(e) => {
                          handleEquipmentItemChange(
                            0,
                            'Stealth',
                            e.target.checked
                          )
                        }}
                      />
                    }
                    label="Stealth"
                  />
                </div>
                <Divider />
                <div className="row">
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.[
                              'Arthrex Tower'
                            ] as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Arthrex Tower',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="Arthrex Tower"
                    />
                  </div>
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.[
                              'Big C-arm'
                            ] as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Big C-arm',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="Big C-arm"
                    />
                  </div>
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.[
                              'Constellation'
                            ] as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Constellation',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="Constellation"
                    />
                  </div>
                </div>
                <Divider />
                <div className="row">
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.[
                              'ENT Cobaltor'
                            ] as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'ENT Cobaltor',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="ENT Cobaltor"
                    />
                  </div>
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.[
                              'Lipo Machine'
                            ] as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Lipo Machine',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="Lipo Machine"
                    />
                  </div>
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.[
                              'Mini C-arm'
                            ] as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Mini C-arm',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="Mini C-arm"
                    />
                  </div>
                </div>
                <Divider />
                <div className="row">
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.[
                              'Myosure Fluent'
                            ] as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Myosure Fluent',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="Myosure Fluent"
                    />
                  </div>
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.[
                              'Opthamology Microscope'
                            ] as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Opthamology Microscope',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="Opthamology Microscope"
                    />
                  </div>
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.[
                              'ORA'
                            ] as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'ORA',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="ORA"
                    />
                  </div>
                </div>
                <Divider />
                <div className="row">
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.[
                              'Spinal Microscope'
                            ] as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Spinal Microscope',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="Spinal Microscope"
                    />
                  </div>
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.[
                              'Stryker Tower'
                            ] as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Stryker Tower',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="Stryker Tower"
                    />
                  </div>
                  <div className="item">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            (equipmentItems?.options?.[0]?.[
                              'Tenex Machine'
                            ] as boolean) ?? false
                          }
                          onChange={(e) => {
                            handleEquipmentItemChange(
                              0,
                              'Tenex Machine',
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label="Tenex Machine"
                    />
                  </div>
                </div>
                <Divider />
                <div className="row">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          (equipmentItems?.options?.[0]?.[
                            'Thunderbeat machine'
                          ] as boolean) ?? false
                        }
                        onChange={(e) => {
                          handleEquipmentItemChange(
                            0,
                            'Thunderbeat machine',
                            e.target.checked
                          )
                        }}
                      />
                    }
                    label="Thunderbeat machine"
                  />
                </div>
              </div>
              <Divider />
              <Notes
                title="Other Notes"
                initialNotes={equipmentItems.notes}
                onNotesChange={(notes) => {
                  setEquipmentItems((prev) => ({
                    ...prev,
                    notes,
                  }))
                }}
              />
            </AccordionDetails>
          </Accordion>

          {/* Instrument Trays */}
          <Accordion className="accordion">
            <AccordionSummary
              expandIcon={<ArrowDownwardIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography>Instrument Trays</Typography>
            </AccordionSummary>
            <AccordionDetails className="accordion-details">
              <div className="details-container col">
                <InstrumentTrays
                  selectedInstruments={instrumentTrays?.instruments}
                  options={instrumentTrayOptions}
                  onChange={(selectedOptions) => {
                    setInstrumentTrays((prev) => ({
                      ...prev,
                      instruments: selectedOptions,
                    }))
                  }}
                  allowCustomOptions={true}
                />
                <Divider />
                <Notes
                  title="Other Notes"
                  initialNotes={instrumentTrays?.notes}
                  onNotesChange={(notes) => {
                    setInstrumentTrays((prev) => ({
                      ...prev,
                      notes,
                    }))
                  }}
                />
              </div>
            </AccordionDetails>
          </Accordion>

          {/* Implants */}
          <Accordion className="accordion">
            <AccordionSummary
              expandIcon={<ArrowDownwardIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography>Implants</Typography>
            </AccordionSummary>

            {/* PASC */}
            {showPascPreferenceCardFeatures && (
              <AccordionDetails className="accordion-details">
                <div className="details-container">
                  <div className="col">
                    <Typography variant="h2">Add Products</Typography>
                    <div className="mt-1">
                      <TextField
                        fullWidth
                        placeholder="Enter product description"
                        variant="outlined"
                        value={implantDescription}
                        onChange={(e) => setImplantDescription(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && implantDescription.trim()) {
                            handleAddProducts('implants', {
                              id: Date.now().toString(),
                              description: implantDescription.trim(),
                            })
                            setImplantDescription('')
                          }
                        }}
                      />
                      <Button
                        variant="contained"
                        onClick={() => {
                          if (implantDescription.trim()) {
                            handleAddProducts('implants', {
                              id: Date.now().toString(),
                              description: implantDescription.trim(),
                            })
                            setImplantDescription('')
                          }
                        }}
                        sx={{ mt: 1 }}
                      >
                        Add
                      </Button>
                    </div>

                    <div
                      className={classNames('implant-products-container', {
                        disabled: selectedProducts.implants?.length === 0,
                      })}
                    >
                      {selectedProducts.implants?.length > 0 ? (
                        <div className="selected-products">
                          {selectedProducts.implants?.map((item) => (
                            <Product
                              item={{
                                id: item.id.toString(),
                                name: item.description,
                              }}
                              key={item.id}
                              onDelete={(itemToDelete) =>
                                handleDeleteProduct(
                                  'implants',
                                  itemToDelete as ProductItem
                                )
                              }
                            />
                          ))}
                        </div>
                      ) : (
                        <>No products added yet.</>
                      )}
                    </div>
                  </div>
                </div>
              </AccordionDetails>
            )}

            {/* Other */}
            {!showPascPreferenceCardFeatures && (
              <AccordionDetails className="accordion-details">
                <div className="details-container">
                  <div className="col">
                    <Typography variant="h2">Add Companies</Typography>
                    <div className="mt-1">
                      <CompanySearchBar
                        handleChangeFormData={(e) =>
                          handleAddCompanies('implants', e)
                        }
                        variant="outlined"
                        required={false}
                        placeholder="Search companies"
                        clearOnSelect={true}
                      />
                    </div>

                    <div
                      className={classNames('implant-products-container', {
                        disabled: selectedCompanies.implants?.length === 0,
                      })}
                    >
                      {selectedCompanies.implants?.length > 0 ? (
                        <div className="selected-products">
                          {selectedCompanies.implants?.map((item) => (
                            <Product
                              item={item}
                              key={item.id}
                              onDelete={(item) =>
                                handleDeleteCompany('implants', item as Option)
                              }
                            />
                          ))}
                        </div>
                      ) : (
                        <>No companies added yet.</>
                      )}
                    </div>
                  </div>
                </div>
              </AccordionDetails>
            )}
          </Accordion>

          {/* Link to Sales Rep */}
          {!showPascPreferenceCardFeatures && (
            <Accordion className="accordion">
              <AccordionSummary
                expandIcon={<ArrowDownwardIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Typography>Link to Sales Reps</Typography>
              </AccordionSummary>
              <AccordionDetails className="accordion-details">
                {[
                  ...selectedCompanies?.biologic,
                  ...selectedCompanies?.implants,
                ].length === 0 && (
                  <div className="mt-2">
                    <Warning
                      type="info"
                      text="Please add companies for implants or biologics to show the vendors."
                    />
                  </div>
                )}

                <div className="mt-2">
                  <Typography variant="h3">Implants and Biologics</Typography>
                </div>
                <AutocompleteInput
                  disabled={
                    !selectedCompanies?.biologic.length &&
                    !selectedCompanies?.implants.length
                  }
                  options={[
                    ...selectedCompanies?.biologic,
                    ...selectedCompanies?.implants,
                  ]}
                  handleChangeFormData={(e) =>
                    setSelectedVendor(e.target.value as Option)
                  }
                  variant="outlined"
                  placeholder="Select Vendor"
                  label="Select Vendor"
                  value={selectedVendor}
                />
                <div
                  className={classNames('row', {
                    wrap: isMobile,
                  })}
                >
                  <AutocompleteInput
                    disabled={
                      !selectedCompanies?.biologic.length &&
                      !selectedCompanies?.implants.length
                    }
                    handleChangeFormData={handleAddSalesRep}
                    options={repsOptions}
                    variant="outlined"
                    placeholder="Search Sales Reps"
                    showEmail
                    label="Search Sales Reps"
                    sx={{
                      mt: 0,
                    }}
                  />
                  <Button
                    disabled={!selectedVendor?.id}
                    className="add-button"
                    variant="contained"
                    fullWidth={isMobile}
                    size="small"
                    onClick={() =>
                      setSalesRepModal({
                        isOpen: true,
                        type: 'rep',
                        company: selectedVendor,
                      })
                    }
                  >
                    Add Sales Rep
                  </Button>
                </div>

                <div className="selected-products">
                  {Object.values(groupedRepsByCompany).map(
                    ({ companyName, reps }) => (
                      <div key={companyName} className="company-group">
                        <div className="header">
                          <Typography variant="h3">{companyName}</Typography>
                          <Typography variant="caption">
                            Notify Preferred Rep
                          </Typography>
                        </div>
                        {reps.map((item) => (
                          <RepItem
                            key={item?.id}
                            rep={item}
                            notifyEmail={item.sendEmail}
                            notifySms={item.sendSms}
                            companyName={companyName}
                            handleSendSurgeryToRep={handleNotifySalesRep}
                            isLoading={false}
                            selectedType="EMAIL"
                            handleDelete={handleDeleteSalesRep}
                          />
                        ))}
                      </div>
                    )
                  )}
                </div>
                <div className="new-rep-container">
                  {isNMRepEnabled && (
                    <>
                      <Divider />
                      <div className="notify-rep">
                        {nMRep?.isSetup ? (
                          <div className="rep-container">
                            <Typography variant="h3">
                              Neuromonitoring Rep
                            </Typography>
                            <RepItem
                              key={nMRep?.email}
                              rep={nMRep}
                              notifyEmail={nMRep.sendEmail}
                              notifySms={nMRep.sendSms}
                              handleSendSurgeryToRep={handleNotifySalesRep}
                              isLoading={false}
                              selectedType="EMAIL"
                              handleDelete={handleDeleteSalesRep}
                            />
                          </div>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={() =>
                              setSalesRepModal({
                                isOpen: true,
                                type: 'Neuromonitoring',
                              })
                            }
                          >
                            Set Up Neuromonitoring Rep
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                  {isPerfusionistRepEnabled && (
                    <>
                      <Divider />
                      <div className="notify-rep">
                        {perfusionistRep?.isSetup ? (
                          <div className="rep-container">
                            <Typography variant="h3">
                              Perfusionist Rep
                            </Typography>
                            <RepItem
                              key={perfusionistRep?.email}
                              rep={perfusionistRep}
                              notifyEmail={perfusionistRep.sendEmail}
                              notifySms={perfusionistRep.sendSms}
                              handleSendSurgeryToRep={handleNotifySalesRep}
                              isLoading={false}
                              selectedType="EMAIL"
                              handleDelete={handleDeleteSalesRep}
                            />
                          </div>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={() =>
                              setSalesRepModal({
                                isOpen: true,
                                type: 'Perfusionist',
                              })
                            }
                          >
                            Set Up Perfusionist Rep
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Biologics */}
          <Accordion className="accordion">
            <AccordionSummary
              expandIcon={<ArrowDownwardIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography>Biologics</Typography>
            </AccordionSummary>

            {/* PASC */}
            {showPascPreferenceCardFeatures && (
              <AccordionDetails className="accordion-details">
                <div className="products-container">
                  <Typography variant="h2">Add Products</Typography>
                  <div className="mt-1">
                    <TextField
                      fullWidth
                      placeholder="Enter product description"
                      variant="outlined"
                      value={biologicDescription}
                      onChange={(e) => setBiologicDescription(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && biologicDescription.trim()) {
                          handleAddProducts('biologic', {
                            id: Date.now().toString(),
                            description: biologicDescription.trim(),
                          })
                          setBiologicDescription('')
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (biologicDescription.trim()) {
                          handleAddProducts('biologic', {
                            id: Date.now().toString(),
                            description: biologicDescription.trim(),
                          })
                          setBiologicDescription('')
                        }
                      }}
                      sx={{ mt: 1 }}
                    >
                      Add
                    </Button>
                  </div>

                  <div
                    className={classNames('implant-products-container', {
                      disabled: selectedProducts.biologic?.length === 0,
                    })}
                  >
                    {selectedProducts.biologic?.length > 0 ? (
                      <div className="selected-products">
                        {selectedProducts.biologic?.map((item) => (
                          <Product
                            item={{
                              id: item.id.toString(),
                              name: item.description,
                            }}
                            key={item.id}
                            onDelete={(itemToDelete) =>
                              handleDeleteProduct(
                                'biologic',
                                itemToDelete as ProductItem
                              )
                            }
                          />
                        ))}
                      </div>
                    ) : (
                      <>No products added yet.</>
                    )}
                  </div>
                </div>
              </AccordionDetails>
            )}

            {/* Other */}
            {!showPascPreferenceCardFeatures && (
              <AccordionDetails className="accordion-details">
                <div className="details-container">
                  <div className="col">
                    <Typography variant="h2">Add Companies</Typography>
                    <div className="mt-1">
                      <CompanySearchBar
                        handleChangeFormData={(e) =>
                          handleAddCompanies('biologic', e)
                        }
                        variant="outlined"
                        required={false}
                        placeholder="Search companies"
                        clearOnSelect={true}
                      />
                    </div>

                    <div
                      className={classNames('implant-products-container', {
                        disabled: selectedCompanies.biologic?.length === 0,
                      })}
                    >
                      {selectedCompanies.biologic?.length > 0 ? (
                        <div className="selected-products">
                          {selectedCompanies.biologic?.map((item) => (
                            <Product
                              item={item}
                              key={item.id}
                              onDelete={(item) =>
                                handleDeleteCompany('biologic', item as Option)
                              }
                            />
                          ))}
                        </div>
                      ) : (
                        <>No companies added yet.</>
                      )}
                    </div>
                  </div>
                </div>
              </AccordionDetails>
            )}
          </Accordion>

          {/* Consumables */}
          <Accordion className="accordion">
            <AccordionSummary
              expandIcon={<ArrowDownwardIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography>Consumables</Typography>
            </AccordionSummary>
            <AccordionDetails className="accordion-details">
              <div className="details-container">
                <div className="row">
                  <div className="col gap-1">
                    <AutocompleteInput
                      handleChangeFormData={(e) =>
                        handleProductSelect(e.target.value as Option)
                      }
                      onTextInputChange={(e) =>
                        debouncedSetSearchValue(e.target.value as string)
                      }
                      value={consumablesSearchValue}
                      options={consumablesOptions}
                      label="Search consumables"
                      variant="outlined"
                      placeholder="Search"
                      isLoading={isLoadingConsumables}
                      clearOnSelect={true}
                    />

                    <TagFilter
                      tags={availableTags}
                      selectedTags={selectedFilterTags}
                      onTagSelect={handleTagFilterSelect}
                    />

                    <DataTable
                      data-testid="consumables-table"
                      tableHeaders={consumablesTableHeaders}
                      tableRows={consumablesTableData || []}
                      isLoading={isLoadingConsumablesProductsData}
                      isErrored={false}
                    />
                  </div>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>
        </div>
      </div>

      <AddSalesRep
        key={salesRepModal.modalId}
        isLoading={isLoadingAddNewRep}
        isOpen={salesRepModal.isOpen}
        type={salesRepModal.type}
        onClose={() => setSalesRepModal({ isOpen: false })}
        onAdd={handleAddNewSalesRep}
        company={salesRepModal.company}
      />

      <QuantityModal
        open={quantityModalOpen}
        onClose={() => setQuantityModalOpen(false)}
        onConfirm={handleMeasurementConfirm}
        product={selectedProduct}
      />
    </div>
  )
}

const Product = ({
  item,
  hasSwitch = false,
  value,
  onDelete,
  onChange,
}: {
  item: Option | ProductItem
  hasSwitch?: boolean
  value?: boolean
  onDelete: (item: Option | ProductItem) => void
  onChange?: (bool: boolean) => void
}) => (
  <div className="product">
    <div className="name">
      {typeof item === 'object' && 'description' in item
        ? item.description
        : item.name}
    </div>
    <div className="actions">
      {hasSwitch && (
        <div className="invite-switch">
          <Typography variant="caption">Notify</Typography>
          <Switch
            checked={value}
            onChange={(e) => onChange?.(e.target.checked)}
          />
        </div>
      )}
      <IconButton onClick={() => onDelete(item)}>
        <DeleteIcon />
      </IconButton>
    </div>
  </div>
)

export default CardView
